import dayjs from "dayjs"
import _ from "lodash"
import { dateToApiString } from "./dateTime"

export type QueryStringMapperDescriptor = {
    queryStringKey: string
    dataType: "string" | "number" | "date" | "string[]" | "boolean" | "dayjs" | "number[]"
}

export type QueryStringSerializationObject = object & {
    [key: string]: string | number | Date | string[] | boolean | object | number[] | dayjs.Dayjs
}

export type QueryStringMapper<T> = Partial<Record<keyof T, QueryStringMapperDescriptor>> & {
    [key: string]: QueryStringMapperDescriptor
}

export const deserializeFromQueryString = <T extends QueryStringSerializationObject>(query: URLSearchParams, mapper: QueryStringMapper<T>) => {
    const obj = {} as T
    Object.keys(mapper).map((prop) => {
        const mapperDesc = mapper[prop]
        const queryStringValue = query.get(mapperDesc.queryStringKey)
        if (queryStringValue) {
            switch (mapperDesc.dataType) {
                case "string":
                    _.set(obj, prop, queryStringValue as string)
                    break
                case "number":
                    _.set(obj, prop, Number(queryStringValue))
                    break
                case "date":
                    _.set(obj, prop, new Date(queryStringValue as string))
                    break
                case "dayjs":
                    _.set(obj, prop, dayjs(queryStringValue as string))
                    break
                case "boolean":
                    _.set(obj, prop, queryStringValue === "1")
                    break
                case "string[]":
                    _.set(obj, prop, query.getAll(mapperDesc.queryStringKey))
                    break
                case "number[]":
                    _.set(
                        obj,
                        prop,
                        queryStringValue.split("-").map((x) => Number(x))
                    )
                    break
            }
        }
    })
    return obj
}

export const serializeToQueryString = <T extends QueryStringSerializationObject>(
    obj: Partial<T>,
    mapper: QueryStringMapper<T>,
    override?: boolean, // if true, existing QueryString will be replaced with brand new instead of appended
    originalQuery?: URLSearchParams // used for creating new query string from original
) => {
    const query = new URLSearchParams()
    if (!override && originalQuery) Object.keys(originalQuery).map((key) => query.append(key, originalQuery.get(key) as string))

    Object.keys(mapper).map((prop) => {
        const mapperDesc = mapper[prop]
        const objValue = _.get(obj, prop)
        if (objValue === undefined || objValue === null) query.delete(mapperDesc.queryStringKey)
        else {
            switch (mapperDesc.dataType) {
                case "string":
                    query.set(mapperDesc.queryStringKey, objValue as string)
                    break
                case "number":
                    query.set(mapperDesc.queryStringKey, objValue.toString())
                    break
                case "date":
                    query.set(mapperDesc.queryStringKey, dateToApiString(objValue as Date))
                    break
                case "dayjs":
                    query.set(mapperDesc.queryStringKey, dateToApiString((objValue as dayjs.Dayjs).toDate()))
                    break
                case "boolean":
                    query.set(mapperDesc.queryStringKey, (objValue as boolean) ? "1" : "0")
                    break
                case "string[]":
                    query.delete(mapperDesc.queryStringKey)
                    ;(objValue as string[]).map((val) => query.append(mapperDesc.queryStringKey, val))
                    break
                case "number[]":
                    query.set(mapperDesc.queryStringKey, (objValue as number[]).join("-"))
                    break
            }
        }
    })
    return query
}
